import { PermissionSet } from "../../rbac/permission_set";
import { ApplicationRemovalTasksPermissions } from "../../rbac/rbac";
import { ApplicationRemovalTaskStatus } from "./application-removal-task-status";

const ApplicationRemovalTaskErrorDetails = [
  "application not found",
  "removed already",
] as const;

export type ApplicationRemovalTaskErrorDetailsLiterals =
  (typeof ApplicationRemovalTaskErrorDetails)[number];

export class ApplicationRemovalTask {
  readonly id: string;
  readonly status: ApplicationRemovalTaskStatus;
  readonly reason: string;
  readonly scheduledRemoveUnixtime: number;
  readonly removalStartedAt?: Date;
  readonly targetApplications: TargetApplication[];
  readonly procedureID: number;
  readonly procedureName: string;
  readonly earliestCreatedAt: Date;
  readonly latestCreatedAt: Date;
  readonly groupId: string;
  readonly errDetail: string | null;
  readonly createdBy: string;
  readonly groupName: string;

  constructor(
    id: string,
    status: ApplicationRemovalTaskStatus,
    reason: string,
    scheduledRemoveUnixtime: number,
    removalStartedAt: Date | undefined,
    targetApplications: TargetApplication[],
    procedureID: number,
    procedureName: string,
    earliestCreatedAt: Date,
    latestCreatedAt: Date,
    groupId: string,
    errDetail: string | null,
    createdBy: string,
    groupName: string,
  ) {
    this.id = id;
    this.status = status;
    this.reason = reason;
    this.scheduledRemoveUnixtime = scheduledRemoveUnixtime;
    this.removalStartedAt = removalStartedAt;
    this.targetApplications = targetApplications;
    this.procedureID = procedureID;
    this.procedureName = procedureName;
    this.earliestCreatedAt = earliestCreatedAt;
    this.latestCreatedAt = latestCreatedAt;
    this.groupId = groupId;
    this.errDetail = errDetail;
    this.createdBy = createdBy;
    this.groupName = groupName;
  }

  static fromJSON(json: ApplicationRemovalTask): ApplicationRemovalTask {
    return new ApplicationRemovalTask(
      json.id,
      json.status,
      json.reason,
      json.scheduledRemoveUnixtime,
      json.removalStartedAt ? new Date(json.removalStartedAt) : undefined,
      json.targetApplications.map((targetApplication) =>
        TargetApplication.fromJSON(targetApplication),
      ),
      json.procedureID,
      json.procedureName,
      new Date(json.earliestCreatedAt),
      new Date(json.latestCreatedAt),
      json.groupId,
      json.errDetail,
      json.createdBy,
      json.groupName,
    );
  }

  isExecuted(): boolean {
    return this.status === "Done" || this.status === "Error";
  }

  resolvePermission(groupId: string): PermissionSet {
    return new PermissionSet(
      this.groupId === groupId ? [...ApplicationRemovalTasksPermissions] : [],
    );
  }
}

export class TargetApplication {
  readonly applicationID: string;
  readonly removedAt?: Date;
  readonly createdAt: Date;
  readonly errDetail: string | null;

  constructor(
    applicationID: string,
    removedAt: Date | undefined,
    createdAt: Date,
    errDetail: string | null,
  ) {
    this.applicationID = applicationID;
    this.removedAt = removedAt;
    this.createdAt = createdAt;
    this.errDetail = errDetail;
  }

  static fromJSON(json: TargetApplication): TargetApplication {
    return new TargetApplication(
      json.applicationID,
      json.removedAt ? new Date(json.removedAt) : undefined,
      new Date(json.createdAt),
      json.errDetail,
    );
  }
}
